<template>
  <div v-loading="isProcessing">
    <ExportWizardFlags
      v-if="1 === activeTab"
      v-model="models.flags"
      :exclude="excludeFlags"
    />
    <ExportWizardReportDate
      v-else-if="2 === activeTab"
      v-model="models.reportDate"
      :date-badges="reportDateBadges"
      :exclude="excludeReportingDates"
    />
    <ExportWizardSubmissionDate
      v-else-if="3 === activeTab"
      v-model="models.submissionDate"
    />
    <ExportWizardCategories
      v-else-if="4 === activeTab"
      v-model="models.exportBy"
      :exclude="excludedCategories"
    />
    <ExportWizardSave v-else-if="5 === activeTab" :payload="exportPayload" />
    <ExportWizardColumns v-else v-model="models.columns" />

    <div class="mt-4 d-flex align-items-center justify-content-center">
      <base-button v-if="activeTab" @click.prevent="activeTab--" type="primary"
        >Previous</base-button
      >
      <base-button
        v-if="activeTab < 5"
        @click.prevent="activeTab++"
        type="primary"
        >Next</base-button
      >
      <base-button v-else @click.prevent="exportData" type="primary"
        >Export</base-button
      >
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'

import moment from 'moment'
import { exportMixin, userMixin } from '@mixins'
import { ErrorService, ReportsService } from '@services'
import ExportWizardColumns from '@components/ExportWizard/ExportWizardColumns'
import ExportWizardReportDate from '@components/ExportWizard/ExportWizardReportDate'
import ExportWizardSubmissionDate from '@components/ExportWizard/ExportWizardSubmissionDate'
import ExportWizardFlags from '@components/ExportWizard/ExportWizardFlags'
import ExportWizardCategories from '@components/ExportWizard/ExportWizardCategories'
import ExportWizardSave from '@components/ExportWizard/ExportWizardSave'

export default {
  mixins: [exportMixin, userMixin],
  props: {
    exportId: null,
    editMode: Boolean,
    flags: Array,
    category: undefined,
    columns: {
      type: Array,
      required: true
    },
    params: Array
  },
  components: {
    ExportWizardColumns,
    ExportWizardReportDate,
    ExportWizardSubmissionDate,
    ExportWizardFlags,
    ExportWizardCategories,
    ExportWizardSave
  },
  data() {
    return {
      isProcessing: false,
      activeTab: 0,
      models: {
        columns: [],
        reportDate: undefined,
        submissionDate: undefined,
        flags: [],
        exportBy: undefined
      },
      reportDateBadges: [],
      excludeFlags: [],
      excludeReportingDates: []
    }
  },
  computed: {
    ...mapGetters(['exportItem']),
    excludedCategories() {
      return [
        {
          key: 'coverholder',
          label: 'Coverholder_Name'
        },
        {
          key: 'tpa',
          label: 'TPA_Name'
        },
        {
          key: 'umr',
          label: 'Unique_Market_Reference_UMR'
        }
      ]
        .filter((item) => {
          return !this.models.columns.includes(item.label)
        })
        .map((item) => item.key)
    },
    exportPayload() {
      const parameters = this.params

      if ('object' == typeof this.models.reportDate) {
        const rdIdx = parameters.findIndex(
          (item) => 'Reporting_Period_End_Date' === item.name
        )

        if (0 <= rdIdx) {
          if (this.models.reportDate) {
            if (this.models.reportDate.from && this.models.reportDate.to) {
              parameters[
                rdIdx
              ].value = `${this.models.reportDate.from},${this.models.reportDate.to}`
              parameters[rdIdx].type = 'Range'
            } else {
              parameters[rdIdx].value = this.models.reportDate
            }
          } else {
            parameters.splice(rdIdx, 1)
          }
        } else if (this.models.reportDate) {
          if (this.models.reportDate.from && this.models.reportDate.to) {
            parameters.push({
              name: 'Reporting_Period_End_Date',
              value: `${this.models.reportDate.from},${this.models.reportDate.to}`,
              type: 'Range'
            })
          } else {
            parameters.push({
              name: 'Reporting_Period_End_Date',
              value: this.models.reportDate
            })
          }
        }
      }

      if ('object' == typeof this.models.submissionDate) {
        const sdIdx = parameters.findIndex(
          (item) => 'modified_on' === item.name
        )

        if (0 <= sdIdx) {
          if (this.models.submissionDate) {
            parameters[
              sdIdx
            ].value = `${this.models.submissionDate.from},${this.models.submissionDate.to}`
            parameters[sdIdx].type = 'Range'
          } else {
            parameters.splice(sdIdx, 1)
          }
        } else if (this.models.submissionDate) {
          parameters.push({
            name: 'modified_on',
            value: `${this.models.submissionDate.from},${this.models.submissionDate.to}`,
            type: 'Range'
          })
        }
      }

      return {
        parameters: parameters,
        role: this.userCompanyRole,
        role_value: this.userCompanyRoleValue,
        export_by: this.models.exportBy,
        columns_to_show: this.models.columns,
        flag: this.models.flags
      }
    }
  },
  methods: {
    async exportData() {
      this.isProcessing = true

      await ReportsService.exportData(this.exportPayload)

      this.isProcessing = false

      this.$toasted.show(
        "Your export is in progress. You will be emailed when it's ready on the Export Log page."
      )

      this.$emit('report-exported', true)
    },
    async checkDatasByColumn(type, column, args = {}) {
      const params = []
      const flags = []

      if ('report-date' === type) {
        const startMonth = moment().startOf('month')
        const endMonth = moment().endOf('month')

        if (args.period && 'prev-month' === args.period) {
          startMonth.subtract(1, 'month')
          endMonth.subtract(1, 'month')
        }

        params.push({
          name: 'Reporting_Period_End_Date',
          type: 'Range',
          value: `${startMonth.format('MM-DD-YYYY')},${endMonth.format(
            'MM-DD-YYYY'
          )}`
        })
      } else if ('flag' === type) {
        if (args.flag) {
          if ('critical' === args.flag) {
            flags.push({
              name: 'Critical_Error_Flag',
              value: 1
            })
          } else if ('non-critical' === args.flag) {
            flags.push({
              name: 'Critical_Error_Flag',
              value: 1
            })
            flags.push({
              name: 'Non_Critical_Flag',
              value: 1
            })
          } else if ('no-errors' === args.flag) {
            flags.push({
              name: 'Critical_Error_Flag',
              value: 0
            })
            flags.push({
              name: 'Non_Critical_Flag',
              value: 0
            })
          }
        }
      }

      return await this.checkParameterDatas([column], params, flags)
    }
  },
  watch: {
    'models.reportDate'(newVal) {
      if (!Array.isArray(newVal)) this.reportDateBadges = []
    },
    exportItem: {
      immediate: true,
      handler(newVal) {
        if (!newVal || !newVal.payload) return false

        this.models.columns = newVal.payload.columns_to_show
        this.models.flags = newVal.payload.flag
        this.models.exportBy = newVal.payload.export_by

        if (newVal.payload.parameters.length)
          newVal.payload.parameters.forEach((item) => {
            if ('Reporting_Period_End_Date' === item.name) {
              this.models.reportDate = {
                from: item.value.split(',')[0],
                to: item.value.split(',')[1]
              }
            } else if ('modified_on' === item.name) {
              this.models.submissionDate = {
                from: item.value.split(',')[0],
                to: item.value.split(',')[1]
              }
            }
          })
      }
    }
  },
  created() {
    // Check `Last Month` reporting end date datas
    this.checkDatasByColumn('report-date', 'Reporting_Period_End_Date', {
      period: 'prev-month'
    }).then((response) => {
      if (!response.data.data.Reporting_Period_End_Date.length)
        this.excludeReportingDates.push('prev-month')
    })

    // Check `Current Month` reporting end date datas
    this.checkDatasByColumn('report-date', 'Reporting_Period_End_Date', {
      period: 'current-month'
    }).then((response) => {
      if (!response.data.data.Reporting_Period_End_Date.length)
        this.excludeReportingDates.push('current-month')
    })

    // Check `Critical` flag datas
    this.checkDatasByColumn('flag', 'Coverholder_Name', {
      flag: 'critical'
    }).then((response) => {
      if (!response.data.data.Coverholder_Name.length)
        this.excludeFlags.push('2')
    })

    // Check `Non-Critical` flag datas
    this.checkDatasByColumn('flag', 'Coverholder_Name', {
      flag: 'non-critical'
    }).then((response) => {
      if (!response.data.data.Coverholder_Name.length)
        this.excludeFlags.push('1')
    })

    // Check `No Errors` flag datas
    this.checkDatasByColumn('flag', 'Coverholder_Name', {
      flag: 'no-errors'
    }).then((response) => {
      if (!response.data.data.Coverholder_Name.length)
        this.excludeFlags.push('0')
    })
  },
  mounted() {
    this.models.columns = this.columns

    if (this.params.length)
      this.params.forEach((item) => {
        if ('Reporting_Period_End_Date' === item.name) {
          if (item.value.includes(',')) {
            this.models.reportDate = {
              from: item.value.split(',')[0],
              to: item.value.split(',')[1]
            }
          } else if (Array.isArray(item.value)) {
            this.reportDateBadges = item.value
            this.models.reportDate = item.value
          }
        }
      })

    ErrorService.stopProcessingOnFail(this)
  }
}
</script>
